<template>
    <div class="animated-background">
        <component
            :is="rendererComponent"
            v-if="rendererComponent"
            v-bind="rendererProps"
            class="animated-background-renderer"
        />
    </div>
</template>

<script>
import {defineAsyncComponent} from "vue";
import {ANIMATED_BACKGROUND_PRESETS} from "@/scripts/sections/common/animatedBackgrounds/presets.js";
import {resolveColor} from "@/scripts/common/helpers.js";

const BACKGROUND_RENDERERS = Object.freeze({
    AURORA: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-aurora" */ "@/scripts/sections/common/animatedBackgrounds/Aurora.vue")),
    LIGHT_RAYS: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-light-rays" */ "@/scripts/sections/common/animatedBackgrounds/LightRays.vue")),
    BEAMS: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-beams" */ "@/scripts/sections/common/animatedBackgrounds/Beams.vue")),
    THREADS: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-threads" */ "@/scripts/sections/common/animatedBackgrounds/Threads.vue")),
    LINE_WAVES: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-line-waves" */ "@/scripts/sections/common/animatedBackgrounds/LineWaves.vue")),
    PARTICLES: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-particles" */ "@/scripts/sections/common/animatedBackgrounds/Particles.vue")),
    DOT_GRID: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-dot-grid" */ "@/scripts/sections/common/animatedBackgrounds/DotGrid.vue")),
    SQUARES: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-squares" */ "@/scripts/sections/common/animatedBackgrounds/Squares.vue")),
    PIXEL_BLAST: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-pixel-blast" */ "@/scripts/sections/common/animatedBackgrounds/PixelBlast.vue")),
    GRAINIENT: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-grainient" */ "@/scripts/sections/common/animatedBackgrounds/Grainient.vue")),
    GRADIENT_BLINDS: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-gradient-blinds" */ "@/scripts/sections/common/animatedBackgrounds/GradientBlinds.vue")),
    SILK: defineAsyncComponent(() => import(/* webpackChunkName: "animated-bg-silk" */ "@/scripts/sections/common/animatedBackgrounds/Silk.vue"))
});

export default {
    name: "AnimatedBackground",
    props: {
        selection: {
            type: Object,
            default: null
        },
        colorPalette: {
            type: Object,
            default: null
        }
    },
    computed: {
        rendererKey() {
            return this.selection?.rendererKey || null;
        },
        rendererComponent() {
            return this.rendererKey ? BACKGROUND_RENDERERS[this.rendererKey] : null;
        },
        selectedPreset() {
            return ANIMATED_BACKGROUND_PRESETS.find(item => item.rendererKey === this.rendererKey) || null;
        },
        resolvedProps() {
            const baseProps = JSON.parse(JSON.stringify(this.selectedPreset?.baseProps || {}));

            this.selection?.colorSlots.forEach(slot => {
                const color = resolveColor(slot.defaultColor, this.colorPalette);
                if (!color || !slot?.propPath) return;
                this.setValueByPath(baseProps, slot.propPath, color);
            });

            return baseProps;
        },
        rendererProps() {
            const props = {
                ...this.resolvedProps,
                className: "animated-background-fill",
                style: {
                    width: "100%",
                    height: "100%"
                }
            };
            this.normalizeRendererProps(props);
            return props;
        }
    },
    methods: {
        normalizeRendererProps(props) {
            if (this.rendererKey === "THREADS" && typeof props.color === "string") {
                props.color = this.hexToNormalizedRgb(props.color);
            }
        },
        hexToNormalizedRgb(hex) {
            const clean = String(hex || "").replace("#", "");
            if (clean.length !== 6) return [1, 1, 1];
            const r = parseInt(clean.slice(0, 2), 16) / 255;
            const g = parseInt(clean.slice(2, 4), 16) / 255;
            const b = parseInt(clean.slice(4, 6), 16) / 255;
            return [r, g, b];
        },
        setValueByPath(target, path, value) {
            const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
            const parts = normalizedPath.split(".").filter(Boolean);
            if (!parts.length) return;

            let cursor = target;
            for (let i = 0; i < parts.length - 1; i++) {
                const part = parts[i];
                const nextPart = parts[i + 1];
                const nextIsIndex = /^\d+$/.test(nextPart);

                if (cursor[part] === undefined || cursor[part] === null) {
                    cursor[part] = nextIsIndex ? [] : {};
                }
                cursor = cursor[part];
            }

            const finalPart = parts[parts.length - 1];
            cursor[finalPart] = value;
        }
    }
};
</script>

<style scoped lang="scss">
.animated-background {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
}

.animated-background-renderer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}
</style>
